import React from "react";
import useStore from "../../../context/useStore";
import vehicleTools from "../../../helpers/vehicleTools";
import tools from "../../../helpers/tools";
import { formatPrestationLabel } from "../../../helpers/prestationTools";
import useTVA from "../../../hooks/useInit/useTVA";
import VehicleLabel from "../../common/VehicleLabel/VehicleLabel";

const fmt = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return "–";
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const SectionTitle = ({ icon, children }) => (
  <div className="d-flex align-items-center mb-3 mt-1">
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 6,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: 10,
        flexShrink: 0,
      }}
    >
      <i className={`fa ${icon}`} style={{ color: "#fff", fontSize: 12 }} />
    </div>
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: "#4a5568",
        letterSpacing: 0.5,
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  </div>
);

const ValueCard = ({ label, value, suffix = "€", accent = false }) => (
  <div className="col-6 col-md-4 col-lg-3 mb-3">
    <div
      style={{
        background: accent
          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          : "#f8f9fa",
        borderRadius: 8,
        padding: "12px 14px",
        height: "100%",
        borderLeft: accent ? "none" : "3px solid #e2e8f0",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: accent ? "rgba(255,255,255,0.7)" : "#a0aec0",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: accent ? "#fff" : "#2d3748",
        }}
      >
        {fmt(value)} {suffix}
      </div>
    </div>
  </div>
);

const DetailRow = ({ label, value, bold = false }) => (
  <div
    className="d-flex justify-content-between align-items-center py-1"
    style={{ borderBottom: "1px solid #f1f3f5" }}
  >
    <span style={{ fontSize: 13, color: "#718096" }}>{label}</span>
    <span
      style={{
        fontSize: 13,
        fontWeight: bold ? 700 : 500,
        color: bold ? "#2d3748" : "#4a5568",
        whiteSpace: "nowrap",
        marginLeft: 16,
      }}
    >
      {value}
    </span>
  </div>
);

const ContractDetailModal = ({ contract, onClose }) => {
  const [state] = useStore();

  const constantData = state.constant.items;
  const { getTVAByCatalog } = useTVA();

  const vehicle = contract.vehicle;
  const catalogUuid = contract.catalog?.uuid || vehicle.catalog?.uuid;
  const { TVA, TVA_INVERT } = getTVAByCatalog(catalogUuid);

  const basePrices = vehicleTools.calcVehiclePrices(
    vehicle,
    TVA,
    TVA_INVERT,
    true,
  );
  const pricePublicTTC = vehicle.priceTTC;
  const investAmtHT = contract.investAmtHT
    ? contract.investAmtHT
    : vehicleTools.calcInvestAmtHT(contract, TVA, TVA_INVERT);
  const priceNetTTC = basePrices.priceTotalTTC;
  const financeRentCalc = vehicleTools.calcFinanceRent(contract, TVA_INVERT);
  const totalRentHTCalc = vehicleTools.calcTotalRentHTCalc(contract);
  const totalRentTTCCalc = vehicleTools.calcPriceTTC(contract.totalRentHT, TVA);
  const applicationFeeCalc = vehicleTools.calcApplicationFee(
    investAmtHT,
    constantData.APPLICATION_FEES,
  );

  let baseRentHT =
    financeRentCalc +
    parseFloat(contract.maintenanceValueFinal) +
    parseFloat(contract.marginTTC);
  baseRentHT = isNaN(baseRentHT) ? 0 : Math.round(baseRentHT * 100) / 100;

  let totalBenefits = 0;
  (contract.benefits || []).forEach((b) => {
    totalBenefits += parseFloat(b.priceHT);
  });

  const statusName = tools.findIn(
    state.constant.items.QUOTATION_STATUS,
    "id",
    contract.status.id,
  ).name;
  const statusColor = tools.getQuotationStatusColor(contract.status.id);

  return (
    <div
      className="position-fixed w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ top: 0, left: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 }}
      onClick={onClose}
    >
      <div
        style={{
          width: "95%",
          maxWidth: 1000,
          maxHeight: "95vh",
          overflowY: "auto",
          borderRadius: 12,
          background: "#fff",
          boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px 12px 0 0",
            padding: "20px 24px",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div
                className="d-flex align-items-center mb-1"
                style={{ gap: 8 }}
              >
                <span
                  className={`badge badge-${statusColor}`}
                  style={{
                    fontSize: 11,
                    padding: "4px 10px",
                    borderRadius: 20,
                  }}
                >
                  {statusName}
                </span>
                <span style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>
                  {contract.reference}
                </span>
              </div>
              {vehicle?.brand && (
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 13 }}>
                  <i className="fa fa-car mr-1" />
                  <VehicleLabel vehicle={vehicle} />
                </div>
              )}
              <div className="d-flex mt-2" style={{ gap: 8 }}>
                <span
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    fontSize: 11,
                    padding: "3px 10px",
                    borderRadius: 20,
                    fontWeight: 500,
                  }}
                >
                  <i className="fa fa-calendar-alt mr-1" />
                  {contract.dur} mois
                </span>
                <span
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    fontSize: 11,
                    padding: "3px 10px",
                    borderRadius: 20,
                    fontWeight: 500,
                  }}
                >
                  <i className="fa fa-road mr-1" />
                  {parseInt(contract.km).toLocaleString("fr-FR")} km
                </span>
                {contract.client?.name && (
                  <span
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      color: "#fff",
                      fontSize: 11,
                      padding: "3px 10px",
                      borderRadius: 20,
                      fontWeight: 500,
                    }}
                  >
                    <i className="fa fa-user mr-1" />
                    {contract.client.name}
                  </span>
                )}
              </div>
            </div>
            <button
              className="btn btn-sm"
              onClick={onClose}
              style={{
                color: "#fff",
                background: "rgba(255,255,255,0.15)",
                borderRadius: 8,
                width: 32,
                height: 32,
                padding: 0,
                lineHeight: "32px",
              }}
            >
              <i className="fa fa-times" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px" }}>
          {/* Véhicule */}
          <SectionTitle icon="fa-car">Véhicule</SectionTitle>
          <div className="row">
            <ValueCard label="Prix pub. TTC" value={pricePublicTTC} />
            <div className="col-6 col-md-4 col-lg-3 mb-3">
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: 8,
                  padding: "12px 14px",
                  height: "100%",
                  borderLeft: "3px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: "#a0aec0",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    marginBottom: 4,
                  }}
                >
                  Remise
                </div>
                <div
                  style={{ fontSize: 16, fontWeight: 700, color: "#2d3748" }}
                >
                  {vehicle.discountPct}% / {fmt(vehicle.discount)} €
                </div>
              </div>
            </div>
            <ValueCard label="Prix net TTC" value={priceNetTTC} accent />
            <ValueCard label="Montant invest HT" value={investAmtHT} />
          </div>

          {/* Loyers */}
          <SectionTitle icon="fa-money-bill-wave">
            Loyers &amp; marges
          </SectionTitle>
          <div className="row">
            <ValueCard label="Marge TTC" value={contract.marginTTC} />
            <ValueCard label="Loyer de base HT" value={baseRentHT} />
            <ValueCard label="Total loyer HT" value={totalRentHTCalc} accent />
            <ValueCard
              label="Total loyer TTC"
              value={totalRentTTCCalc}
              accent
            />
          </div>

          {/* Financement & coûts */}
          <SectionTitle icon="fa-chart-line">
            Financement &amp; coûts
          </SectionTitle>
          <div className="row mb-3">
            <div className="col-md-4 mb-3">
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: 8,
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#667eea",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    marginBottom: 8,
                  }}
                >
                  Financement
                </div>
                <DetailRow label="Taux" value={`${contract.financeRate} %`} />
                <DetailRow
                  label="Apport 1er loyer"
                  value={`${fmt(contract.firstRentInput)} €`}
                />
                <DetailRow
                  label="Frais de dossier"
                  value={`${fmt(contract.applicationFee || applicationFeeCalc)} €`}
                />
                <DetailRow
                  label="Loyer financier"
                  value={`${fmt(financeRentCalc)} €`}
                  bold
                />
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: 8,
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#667eea",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    marginBottom: 8,
                  }}
                >
                  Valeur de rachat
                </div>
                <DetailRow label="Taux VR" value={`${contract.rateVR} %`} />
                <DetailRow
                  label="Valeur de rachat"
                  value={`${fmt(contract.repurchaseValueFinal)} €`}
                  bold
                />
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: 8,
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#667eea",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    marginBottom: 8,
                  }}
                >
                  Maintenance
                </div>
                <DetailRow
                  label="Coût"
                  value={`${fmt(contract.maintenanceValueFinal)} €`}
                />
                <DetailRow
                  label="Taux"
                  value={`${contract.rateMaintenance} %`}
                />
              </div>
            </div>
          </div>

          {/* Garanties */}
          <SectionTitle icon="fa-shield-alt">
            Garanties &amp; options
          </SectionTitle>
          <div className="row">
            <ValueCard
              label="Véhicule relais"
              value={contract.replacementVehicleCost}
            />
            <ValueCard
              label="Assurance Prévoyance"
              value={contract.insuranceHT}
            />
            <ValueCard
              label="Option Sérénité"
              value={contract.insuranceFinLoss}
            />
            <ValueCard label="Autres prestations" value={totalBenefits} />
          </div>

          {/* Prestations */}
          {(contract.benefits || []).length > 0 && (
            <>
              <SectionTitle icon="fa-list">Prestations</SectionTitle>
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: 8,
                  padding: "14px 16px",
                  maxWidth: 420,
                  marginBottom: 16,
                }}
              >
                {contract.benefits.map((b, i) => (
                  <DetailRow
                    key={i}
                    // LS-690 (review sprint20) — même mise en forme que les PDF
                    // du devis : la même prestation ne doit pas s'écrire
                    // « Caution » sur le PDF et « CAUTION » ici.
                    label={formatPrestationLabel(b.name)}
                    value={`${fmt(b.priceHT)} €`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Mentions */}
          {(contract.mentions || []).length > 0 && (
            <>
              <SectionTitle icon="fa-quote-left">Mentions</SectionTitle>
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: 8,
                  padding: "14px 16px",
                  marginBottom: 16,
                }}
              >
                {contract.mentions.map((m, i) => (
                  <div key={i} className="d-flex align-items-start mb-1">
                    <i
                      className="fa fa-circle mr-2"
                      style={{ fontSize: 5, color: "#cbd5e0", marginTop: 6 }}
                    />
                    <span style={{ fontSize: 13, color: "#718096" }}>
                      {m.name}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Commentaire */}
          {contract.comment && (
            <>
              <SectionTitle icon="fa-comment">Commentaire</SectionTitle>
              <div
                style={{
                  background: "#fffbeb",
                  borderRadius: 8,
                  padding: "14px 16px",
                  borderLeft: "3px solid #f6ad55",
                }}
              >
                <p
                  className="mb-0"
                  style={{
                    fontSize: 13,
                    color: "#744210",
                    fontStyle: "italic",
                  }}
                >
                  {contract.comment}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractDetailModal;
